/**
 * Display/input formatting only — no summing. Multi-value rounding (invoice totals, VAT)
 * is server-side business logic per docs/design/05-invoicing-vat.md and lands with Phase 2,
 * mirrored here from shared fixtures rather than reimplemented ad hoc.
 */
export function formatMoney(amount: string, currency = "GBP", locale = "en-GB"): string {
  const value = Number(amount);
  if (Number.isNaN(value)) return amount;
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
}

/** Normalises free-typed input ("1,234.5", "  12 ") into a canonical "NNNN.NN" string, or null if not a number. */
export function parseMoneyInput(raw: string): string | null {
  const cleaned = raw.replace(/[£$€,\s]/g, "");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  if (Number.isNaN(value)) return null;
  return value.toFixed(2);
}

/* Integer-pence arithmetic (Phase 2a item 14): every client-side money aggregation goes
   through these — never bare float ops, whose representation error can drift a display
   total by a penny. BigInt internally so products (quantity thousandths × pence) can't
   overflow Number's 2^53 either. Rounding is half-up away from zero, mirroring the
   server's single source of truth (backend invoice_totals.round_half_up). */

const MONEY_RE = /^-?\d+(\.\d{1,2})?$/;
const QUANTITY_RE = /^-?\d+(\.\d{1,3})?$/;

/** "1234.56" → 123456n; null when not a plain ≤2dp money string. */
function toPence(value: string): bigint | null {
  if (!MONEY_RE.test(value)) return null;
  const negative = value.startsWith("-");
  const [units = "0", frac = ""] = (negative ? value.slice(1) : value).split(".");
  const pence = BigInt(units) * 100n + BigInt(frac.padEnd(2, "0") || "0");
  return negative ? -pence : pence;
}

function fromPence(pence: bigint): string {
  const negative = pence < 0n;
  const abs = negative ? -pence : pence;
  const units = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${units.toString()}.${frac}`;
}

/** Divide with half-up-away-from-zero rounding. */
function divideHalfUp(numerator: bigint, divisor: bigint): bigint {
  const negative = numerator < 0n !== divisor < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = divisor < 0n ? -divisor : divisor;
  const q = (n + d / 2n) / d;
  return negative ? -q : q;
}

/** Sums money strings exactly; unparseable entries count as zero (mirrors the old
 * `Number(x) || 0` tolerance without inheriting its float drift). */
export function sumMoney(values: string[]): string {
  let total = 0n;
  for (const value of values) total += toPence(value) ?? 0n;
  return fromPence(total);
}

export function subtractMoney(a: string, b: string): string {
  return fromPence((toPence(a) ?? 0n) - (toPence(b) ?? 0n));
}

/** quantity (≤3dp) × unit price → money, rounded half-up to the penny — the client-side
 * mirror of the server's line_total rule, for the live editor preview only. */
export function multiplyMoney(quantity: string, unitPrice: string): string {
  const pence = toPence(unitPrice);
  if (pence === null || !QUANTITY_RE.test(quantity)) return "0.00";
  const negative = quantity.startsWith("-");
  const [units = "0", frac = ""] = (negative ? quantity.slice(1) : quantity).split(".");
  let thousandths = BigInt(units) * 1000n + BigInt(frac.padEnd(3, "0") || "0");
  if (negative) thousandths = -thousandths;
  return fromPence(divideHalfUp(pence * thousandths, 1000n));
}

/** rate% (≤2dp) of amount, rounded half-up to the penny — mirrors the server's
 * VAT-on-subtotal rule for the editor preview. */
export function percentOf(amount: string, ratePct: string): string {
  const pence = toPence(amount);
  const rateHundredths = toPence(ratePct); // ≤2dp percentage parses identically to money
  if (pence === null || rateHundredths === null) return "0.00";
  return fromPence(divideHalfUp(pence * rateHundredths, 10000n));
}
